import { JURISDICTIONS, CORRIDORS } from "@/data/jurisdictions";

const TABS = ["All", ...CORRIDORS];

// "The ledger" — every supported jurisdiction as a full-width row list.
// Corridor tabs filter the rows and (via the page) dim non-matching map countries.
export const AtlasLedger = ({ corridor, onCorridorChange, onSelect }) => {
  const rows = JURISDICTIONS.filter((j) => corridor === "All" || j.corridor === corridor);

  const handleSelect = (j) => {
    onSelect(j);
    document.getElementById("atlas")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="ledger" data-testid="ledger-section" className="bg-canvas border-t border-charcoal/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">The ledger</h2>
            <p className="mt-3 font-sans text-sm md:text-base text-charcoal/60">
              Every jurisdiction we support, by corridor.
            </p>
          </div>

          {/* Corridor tabs — underlined text, not pills */}
          <div data-testid="ledger-tabs" role="tablist" aria-label="Filter ledger by corridor" className="flex flex-wrap gap-x-6 gap-y-2">
            {TABS.map((tab) => {
              const active = tab === corridor;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`ledger-tab-${tab.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => onCorridorChange(tab)}
                  className={`pb-1 font-sans text-[11px] tracking-[0.18em] uppercase border-b-2 transition-colors duration-200 ${
                    active
                      ? "text-charcoal border-gold"
                      : "text-charcoal/45 border-transparent hover:text-charcoal hover:border-charcoal/30"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rows */}
        <ul className="mt-12 border-b border-charcoal/10">
          {rows.map((j, i) => (
            <li key={j.slug}>
              <button
                type="button"
                data-testid={`ledger-row-${j.slug}`}
                onClick={() => handleSelect(j)}
                className="group w-full text-left flex items-baseline gap-5 md:gap-8 px-2 md:px-4 py-5 border-t border-charcoal/10 transition-colors duration-200 hover:bg-[#F6F1E4] focus-visible:bg-[#F6F1E4] focus-visible:outline-none"
              >
                <span className="shrink-0 w-8 font-serif text-sm text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={`shrink-0 font-serif text-lg md:text-xl text-charcoal ${j.sub ? "pl-6" : ""}`}>
                  {j.name}
                  {j.sub && (
                    <span className="ml-3 inline-block align-middle font-sans text-[9px] tracking-[0.18em] uppercase text-gold border border-gold/50 px-2 py-0.5">
                      Free zone
                    </span>
                  )}
                </span>
                <span className="hidden sm:block shrink-0 font-sans text-[10px] tracking-[0.18em] uppercase text-charcoal/45">
                  {j.corridor}
                </span>
                <span className="hidden md:block flex-1 min-w-0 truncate font-sans text-sm text-charcoal/55">
                  {j.line}
                </span>
                <span
                  aria-hidden="true"
                  className="ml-auto shrink-0 font-serif text-lg text-charcoal/50 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-charcoal"
                >
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
